import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Tooltip,
  Typography,
  IconButton,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { leaveServices } from '../../services/leaveServices';
import { LEAVE_STATUS, LEAVE_STATUS_OPTIONS } from '../../constants/leaveStatus';
import { LEAVE_TYPE, LEAVE_TYPE_LABELS, isHalfDay } from '../../constants/leaveType';
import { useSnackbar } from '../../utils/useSnackbar';
import TableSkeleton from '../common/TableSkeleton';
import PageHeader from '../ui/PageHeader';
import EditLeaveDialog from './EditLeaveDialog';
import RequestCancellationDialog from './RequestCancellationDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import '../../css/Leave.css';
import '../../css/Button.css';
import { notificationTrackerService } from '../../services/notificationTrackerService';

const HISTORY_ROLES = ['MANAGE_JOBS', 'ADMIN', 'SUPER_ADMIN'];

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

const toDDMMYYYY = (val) => {
  if (!val) return '';
  const datePart = val.split('T')[0];
  return datePart.split('-').reverse().join('/');
};

const statusColor = (status) => {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'TL_APPROVED': return 'info';
    case 'REJECTED': return 'error';
    case 'CANCELLED': return 'default';
    case 'CANCELLATION_REQUESTED': return 'warning';
    default: return 'warning';
  }
};

const LeaveHistory = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const userRoles = useMemo(() => userData?.roles ?? [], [userData]);
  const userId = userData?.id;
  const navigate = useNavigate();

  const canAccess = useMemo(
    () => userRoles.some((r) => HISTORY_ROLES.includes(r)),
    [userRoles]
  );

  // ── Server-side state ──────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState(LEAVE_STATUS.ALL);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState(null);
  const [requestCancelDialogOpen, setRequestCancelDialogOpen] = useState(false);
  const [leaveToRequestCancel, setLeaveToRequestCancel] = useState(null);
  const [requestCancelLoading, setRequestCancelLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);
  const isSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // ── Debounce search ────────────────────────────────────────────────────
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const val = search.trim();
      if (val === '' || val.length > 2) {
        setDebouncedSearch(val);
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchHistory = async () => {
    if (!userId || !canAccess) return;
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'createdAt';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const data = await leaveServices.getAllLeaves(
        userId,
        userRoles,
        status,
        startDate,
        endDate,
        paginationModel.page,
        paginationModel.pageSize,
        debouncedSearch,
        sortField,
        sortDir,
      );
      setRecords(data?.content ?? []);
      setRowCount(data?.totalElements ?? 0);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRecords([]);
        setRowCount(0);
      } else {
        const msg = err?.response?.data?.message || err?.response?.data || 'Failed to load leave history.';
        showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
        setRecords([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRoles, canAccess, paginationModel, debouncedSearch, sortModel, status, startDate, endDate]);

  const handleRefresh = () => {
    let changed = false;
    if (search !== '') { setSearch(''); changed = true; }
    if (debouncedSearch !== '') { setDebouncedSearch(''); changed = true; }
    if (status !== LEAVE_STATUS.ALL) { setStatus(LEAVE_STATUS.ALL); changed = true; }
    if (startDate !== '') { setStartDate(''); changed = true; }
    if (endDate !== '') { setEndDate(''); changed = true; }
    if (paginationModel.page !== 0) { setPaginationModel(prev => ({ ...prev, page: 0 })); changed = true; }

    if (!changed) {
      fetchHistory();
    }
  };

  // ── Sort handler ───────────────────────────────────────────────────────
  const handleSortModelChange = (newModel) => {
    if (isSortFirstRender.current) {
      isSortFirstRender.current = false;
      if (!newModel?.length) return;
    }
    const next = newModel?.[0];
    const current = sortModel?.[0];
    if (next?.field === current?.field && next?.sort === current?.sort) return;
    if (!newModel?.length && sortModel.length > 0) {
      const currentField = sortModel[0].field;
      const isColumnDefined = columns.some(c => c.field === currentField);

      if (!isColumnDefined) {
        return;
      }
    }
    setSortModel(newModel);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedLeave(null);
    showSnackbar('Leave request updated successfully!', 'success');
    fetchHistory();
  };

  const handleCancelClick = (leave) => {
    setLeaveToCancel(leave);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setLeaveToCancel(null);
  };

  const confirmCancel = async () => {
    if (leaveToCancel && userId) {
      try {
        await leaveServices.cancelLeaveRequest(leaveToCancel.id, userId);
        showSnackbar('Leave request cancelled successfully!', 'success');
        fetchHistory();
      } catch (error) {
        const msg = error?.response?.data || error?.response?.data?.message || 'Failed to cancel leave request.';
        showSnackbar(msg, 'error');
      }
    }
    handleCloseCancelDialog();
  };

  const handleRequestCancelClick = (leave) => {
    setLeaveToRequestCancel(leave);
    setRequestCancelDialogOpen(true);
  };

  const handleConfirmRequestCancel = async (reason) => {
    if (!leaveToRequestCancel || !userId) return;
    try {
      setRequestCancelLoading(true);
      await leaveServices.requestLeaveCancellation(leaveToRequestCancel.id, userId, reason);
      showSnackbar('Leave cancellation requested successfully!', 'success');

      fetchHistory();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data || 'Failed to request leave cancellation.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setRequestCancelLoading(false);
      setRequestCancelDialogOpen(false);
      setLeaveToRequestCancel(null);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: 'userName',
        headerName: 'Applicant',
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={params.value ?? ''}>
            <span className="leave-ellipsis">{params.value ?? '—'}</span>
          </Tooltip>
        ),
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 0.9,
        renderCell: (params) => fmtDate(params.value),
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        flex: 0.9,
        renderCell: (params) => fmtDate(params.value),
      },
      {
        field: 'leaveType',
        headerName: 'Type',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const type = params.value || LEAVE_TYPE.FULL_DAY;
          const half = isHalfDay(type);
          return (
            <Chip
              label={LEAVE_TYPE_LABELS[type] ?? type}
              size="small"
              className={`leave-type-chip ${half ? 'leave-type-chip--half' : 'leave-type-chip--full'}`}
            />
          );
        },
      },
      {
        field: 'reason',
        headerName: 'Reason',
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={params.value ?? ''}>
            <span className="leave-ellipsis">
              {params.value ?? '—'}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: (params) => {
          const isCancellation = params.value === LEAVE_STATUS.CANCELLATION_REQUESTED;
          const isTlApproved = params.value === LEAVE_STATUS.TL_APPROVED;
          const label = isCancellation
            ? 'Cancel Req.'
            : isTlApproved
            ? 'TL Approved'
            : (params.value ?? '—');
          const tooltip = isTlApproved
            ? (params.row.tlActionByName ? `Approved by TL: ${params.row.tlActionByName} (Awaiting Admin approval)` : 'Approved by TL (Awaiting Admin approval)')
            : '';

          return (
            <Tooltip title={tooltip}>
              <Chip
                label={label}
                color={statusColor(params.value)}
                size="small"
                sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'actionByName',
        headerName: 'Processed By',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const isTlApproved = params.row.status === LEAVE_STATUS.TL_APPROVED;
          const displayName = isTlApproved
            ? (params.row.tlActionByName || 'TL')
            : (params.value || params.row.tlActionByName || '—');
          const fullTooltip = params.row.tlActionByName && params.value && params.row.tlActionByName !== params.value
            ? `TL: ${params.row.tlActionByName}\nAdmin: ${params.value}`
            : displayName;
          return (
            <Tooltip title={fullTooltip}>
              <span className="leave-ellipsis">{displayName}</span>
            </Tooltip>
          );
        },
      },
      {
        field: 'actionByComment',
        headerName: 'Comment',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const isTlApproved = params.row.status === LEAVE_STATUS.TL_APPROVED;
          const displayComment = isTlApproved
            ? (params.row.tlActionByComment || '—')
            : (params.value || params.row.tlActionByComment || '—');
          const fullTooltip = params.row.tlActionByComment && params.value && params.row.tlActionByComment !== params.value
            ? `TL Comment: ${params.row.tlActionByComment}\nAdmin Comment: ${params.value}`
            : displayComment;
          return (
            <Tooltip title={fullTooltip}>
              <span className="leave-ellipsis">
                {displayComment}
              </span>
            </Tooltip>
          );
        },
      },
      ...(!userRoles.includes('SUPER_ADMIN') ? [{
        field: 'actions',
        headerName: 'Actions',
        flex: 1.1,
        sortable: false,
        renderCell: (params) => {
          const isOwn = params.row.userId === userId;
          const isPending = params.row.status === LEAVE_STATUS.PENDING && isOwn;
          const isTlApproved = params.row.status === LEAVE_STATUS.TL_APPROVED && isOwn;
          const isApproved = params.row.status === LEAVE_STATUS.APPROVED && isOwn;
          const isCancellationRequested = params.row.status === LEAVE_STATUS.CANCELLATION_REQUESTED && isOwn;
          const canCancelDirectly = (isPending || isTlApproved) && isOwn;

          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Tooltip title={isPending ? "Edit" : "Only your own pending leaves can be edited"}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    className="icon-button"
                    onClick={() => handleEditClick(params.row)}
                    disabled={!isPending}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {isApproved ? (
                <Tooltip title="Request Cancellation">
                  <span>
                    <IconButton
                      size="small"
                      color="warning"
                      className="icon-button"
                      onClick={() => handleRequestCancelClick(params.row)}
                    >
                      <EventBusyRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={canCancelDirectly ? "Cancel Leave" : isCancellationRequested ? "Cancellation request pending approval" : "Only your own pending leaves can be cancelled"}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      className="icon-button"
                      onClick={() => handleCancelClick(params.row)}
                      disabled={!canCancelDirectly}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      }] : []),
    ],
    [userId, userRoles]
  );

  // ── Empty state ────────────────────────────────────────────────────────
  const renderEmptyState = () => {
    const isFiltered = status !== LEAVE_STATUS.ALL || debouncedSearch !== '' || startDate !== '' || endDate !== '';
    return (
      <div className="leave-empty-state">
        <div className="leave-empty-icon-wrap">
          <EventBusyRoundedIcon className="leave-empty-icon" />
        </div>
        <Typography variant="h6" className="leave-empty-title">
          {isFiltered ? 'No Matches Found' : 'No History Found'}
        </Typography>
        <Typography className="leave-empty-subtitle">
          {isFiltered
            ? 'We couldn\'t find any leaves matching your current search or status filter.'
            : 'There are no processed leave requests to show for your scope yet.'}
        </Typography>
      </div>
    );
  };

  // ── Access guard ───────────────────────────────────────────────────────
  if (!canAccess) {
    return (
      <div className="leave-page-container">
        <PageHeader
          title="Leave History"
          left={
            <IconButton onClick={() => navigate(-1)} size="medium">
              <ArrowBackIcon />
            </IconButton>
          }
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <Typography color="text.secondary">
            You do not have permission to view this page.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Leave History"
        left={
          <IconButton onClick={() => navigate(-1)} size="medium">
            <ArrowBackIcon />
          </IconButton>
        }
        right={
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {SnackbarComponent}

      <Paper elevation={0} className="leave-card">
        {/* Header strip */}
        <div className="leave-card-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Typography className="leave-card-header-title" variant="subtitle1">
              Leave
            </Typography>
          </Box>

          {/* Filters and Search box */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 500 }}>From:</Typography>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                className="leave-date-input"
                title="Start Date"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 500 }}>To:</Typography>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                className="leave-date-input"
                title="End Date"
              />
            </Box>
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              size="small"
              sx={{
                width: 155,
                minWidth: 155,
                maxWidth: 155,
                height: 32,
                bgcolor: '#fff',
                borderRadius: 1,
                '& .MuiSelect-select': {
                  py: '4px',
                  fontSize: '0.825rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {LEAVE_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="leave-search-input"
            />
          </Box>
        </div>

        {/* Content */}
        {loading ? (
          <Box sx={{ p: 2 }}>
            <TableSkeleton rows={5} columns={6} />
          </Box>
        ) : !loading && rowCount === 0 ? (
          renderEmptyState()
        ) : (
          <div className="leave-data-table">
            <div className="leave-data-grid">
              <DataGrid
                rows={records}
                columns={columns}
                getRowId={(row) => row.id}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                sx={{
                  height: '100%',
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(4,88,100,0.04)' },
                  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(4,88,100,0.03)' },
                }}
              />
            </div>
          </div>
        )}
      </Paper>

      <EditLeaveDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        leave={selectedLeave}
        onSuccess={handleEditSuccess}
      />

      <ConfirmationDialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        onConfirm={confirmCancel}
        title="Cancel Leave Request"
        message="Are you sure you want to cancel this leave request?"
        cancelLabel="No"
        confirmLabel="Yes, Cancel"
        isDelete={true}
      />

      <RequestCancellationDialog
        open={requestCancelDialogOpen}
        leave={leaveToRequestCancel}
        onClose={() => {
          setRequestCancelDialogOpen(false);
          setLeaveToRequestCancel(null);
        }}
        onSubmit={handleConfirmRequestCancel}
        loading={requestCancelLoading}
      />
    </div>
  );
};

export default LeaveHistory;
